import { useCallback, useRef, useState } from "react";
import { ChatPanel, ChatPanelHandle } from "@/components/chat/ChatPanel";
import { InstrumentPanel } from "@/components/instrument/InstrumentPanel";
import { SessionPanel } from "@/components/sessions/SessionPanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useLyria } from "@/hooks/use-lyria";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import { MessageSquare, Clock, Disc3 } from "lucide-react";

function getPlaybackLabel(state: ReturnType<typeof useLyria>["state"]): string {
  switch (state) {
    case "playing":
      return "Playing";
    case "starting":
      return "Starting";
    case "buffering":
      return "Buffering";
    case "paused":
      return "Paused";
    case "connecting":
      return "Connecting";
    default:
      return "Ready";
  }
}

export function MainLayout() {
  const chatPanelRef = useRef<ChatPanelHandle | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);

  const handlePromptFiltered = useCallback((filteredPrompt: string) => {
    console.log("[MainLayout] Prompt filtered, attempting auto-rewrite");
    chatPanelRef.current?.handleFilteredPrompt(filteredPrompt);
  }, []);

  const {
    state,
    audioLevelRef,
    analyserNode,
    config,
    currentPrompts,
    volume,
    setPrompts,
    setPromptsWithMode,
    updateConfig,
    setVolume,
    primeAudio,
    play,
    pause,
    resetContext,
  } = useLyria({ onPromptFiltered: handlePromptFiltered });

  const playbackLabel = getPlaybackLabel(state);

  return (
    <div className="h-screen w-full overflow-hidden bg-black">
      <header className="pointer-events-none fixed inset-x-0 top-0 z-50 px-4 py-3 sm:px-6">
        <div className="pointer-events-auto mx-auto flex max-w-[1720px] items-center justify-between gap-3 rounded-lg border border-white/8 bg-black/45 px-4 py-2.5 shadow-2xl shadow-black/25 backdrop-blur-xl">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/8">
              <Disc3 className={`h-4 w-4 ${state === "playing" ? "animate-spin text-cyan-300" : "text-white/70"}`} />
            </div>
            <div className="min-w-0">
              <p className="truncate text-xs uppercase tracking-[0.26em] text-white/42">SonicForge</p>
              <div className="mt-0.5 flex items-center gap-2 text-sm text-white/72">
                <span className={`h-2 w-2 rounded-full ${state === "playing" ? "bg-emerald-400" : "bg-white/35"}`} />
                <span>{playbackLabel}</span>
                <span className="text-white/24">/</span>
                <span className="truncate text-white/46">
                  {currentPrompts.length > 0 ? `${currentPrompts.length} active prompts` : "instrument ready"}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setChatOpen(true)}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/6 px-3 text-sm text-white/72 transition-all hover:bg-white/10 hover:text-white"
              title="Open chat"
            >
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Chat Guide</span>
            </button>
            <button
              onClick={() => setSessionsOpen(true)}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/6 px-3 text-sm text-white/72 transition-all hover:bg-white/10 hover:text-white"
              title="Open sessions"
            >
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Sessions</span>
            </button>
          </div>
        </div>
      </header>

      <ErrorBoundary>
        <InstrumentPanel
          lyriaState={state}
          audioLevelRef={audioLevelRef}
          analyserNode={analyserNode}
          config={config}
          currentPrompts={currentPrompts}
          volume={volume}
          onSetPromptsWithMode={setPromptsWithMode}
          onUpdateConfig={updateConfig}
          onVolumeChange={setVolume}
          onPrimeAudio={primeAudio}
          onPlay={play}
          onPause={pause}
          onResetContext={resetContext}
        />
      </ErrorBoundary>

      <Sheet open={chatOpen} onOpenChange={setChatOpen}>
        <SheetContent side="left" className="w-[400px] sm:max-w-[400px] p-0 bg-black/95 border-white/5">
          <SheetTitle className="sr-only">Chat</SheetTitle>
          <ErrorBoundary>
            <ChatPanel
              ref={chatPanelRef}
              lyriaState={state}
              currentPrompts={currentPrompts}
              onSetPrompts={setPrompts}
              onSetPromptsWithMode={setPromptsWithMode}
              onUpdateConfig={updateConfig}
              onPlay={play}
            />
          </ErrorBoundary>
        </SheetContent>
      </Sheet>

      <Sheet open={sessionsOpen} onOpenChange={setSessionsOpen}>
        <SheetContent side="right" className="w-[350px] sm:max-w-[350px] p-0 bg-black/95 border-white/5">
          <SheetTitle className="sr-only">Sessions</SheetTitle>
          <ErrorBoundary>
            <SessionPanel />
          </ErrorBoundary>
        </SheetContent>
      </Sheet>
    </div>
  );
}
